import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Container from '../components/common/Container';
import LessonPreview from '../components/lesson/LessonPreview';
import StoryLesson from '../components/lesson/StoryLesson';
import Year8EnglishLesson from '../components/lesson/Year8EnglishLesson';
import Year8MathsLesson from '../components/lesson/Year8MathsLesson';
import InteractiveModal from '../components/lesson/InteractiveModal';
import InstantFeedbackPreview from '../components/feedback/InstantFeedbackPreview';
import {
  SparklesIcon,
  TrophyIcon,
  FireIcon,
  PuzzlePieceIcon,
  StarIcon,
  BoltIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  AcademicCapIcon,
  GlobeAltIcon,
  ClockIcon,
  BookOpenIcon,
  CalculatorIcon,
  BeakerIcon,
  PlayIcon,
} from '@heroicons/react/24/outline';

type SubjectTab = 'english' | 'maths' | 'science';
type LessonType = 'story' | 'english' | 'maths';

const ForStudents: React.FC = () => {
  const [isLessonModalOpen, setIsLessonModalOpen] = useState(false);
  const [activeLessonType, setActiveLessonType] = useState<LessonType>('story');
  const [activeSubjectTab, setActiveSubjectTab] = useState<SubjectTab>('english');

  const features = [
    {
      icon: ClockIcon,
      title: 'Go At Your Own Pace',
      description: 'No rushing, no falling behind. Take your time to really understand each concept before moving on.',
    },
    {
      icon: BoltIcon,
      title: 'Always-On Support',
      description: 'Stuck at 10pm? No problem. Get instant hints, explanations, and help whenever you need it.',
    },
    {
      icon: AcademicCapIcon,
      title: 'True Mastery',
      description: "We don't just help you pass — we help you actually understand and remember what you learn.",
    },
    {
      icon: PuzzlePieceIcon,
      title: 'Adapts to You',
      description: 'Questions that match your level. Not too easy, not too hard — just right for where you are.',
    },
    {
      icon: FireIcon,
      title: 'Stay Motivated',
      description: 'Build streaks, earn XP, and unlock achievements that make you want to keep learning.',
    },
    {
      icon: TrophyIcon,
      title: 'See Your Growth',
      description: 'Watch your mastery bars fill up as you conquer each topic. Your progress is always visible.',
    },
  ];

  const learningModes = [
    {
      title: 'Practice Mode',
      description: 'Learn at your own pace with unlimited tries and helpful hints',
      features: ['No time pressure', 'Step-by-step guidance', 'Instant explanations'],
      gradient: 'from-[var(--color-electric-blue)] to-[var(--color-bright-cyan)]',
    },
    {
      title: 'Challenge Mode',
      description: 'Test your skills with timed challenges and earn bonus XP',
      features: ['Time-based rewards', 'Difficulty bonuses', 'Combo multipliers'],
      gradient: 'from-[var(--color-violet)] to-[var(--color-electric-blue)]',
    },
    {
      title: 'Adventure Mode',
      description: 'Embark on learning quests and unlock story chapters',
      features: ['Story-driven learning', 'Character customization', 'Special rewards'],
      gradient: 'from-[var(--color-emerald)] to-[var(--color-bright-cyan)]',
    },
  ];

  const subjects = [
    { name: 'Mathematics', mastery: 78, color: '#0ea5e9' },
    { name: 'English', mastery: 85, color: '#8b5cf6' },
    { name: 'Science', mastery: 72, color: '#10b981' },
    { name: 'History', mastery: 90, color: '#f59e0b' },
    { name: 'Geography', mastery: 65, color: '#06b6d4' },
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="pt-20 pb-12 bg-gradient-to-b from-[var(--color-midnight)] to-[var(--color-dark-navy)] overflow-hidden">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="grid lg:grid-cols-2 gap-12 items-center"
          >
            <div>
              <h1 className="text-5xl md:text-6xl font-black text-white mb-6">
                Learn at Your Own Pace. <span className="text-gradient">Master Everything.</span>
              </h1>
              <p className="text-xl text-[var(--color-light-gray)] mb-8">
                No pressure. No judgement. Just you, learning the way that works best for you —
                with support whenever you need it, available 24/7.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3">
                  <ClockIcon className="w-6 h-6 text-[var(--color-electric-blue)]" />
                  <span className="text-white">Learn anytime — morning, night, or weekends</span>
                </div>
                <div className="flex items-center gap-3">
                  <TrophyIcon className="w-6 h-6 text-[var(--color-emerald)]" />
                  <span className="text-white">Actually master topics, not just pass tests</span>
                </div>
                <div className="flex items-center gap-3">
                  <StarIcon className="w-6 h-6 text-[var(--color-amber)]" />
                  <span className="text-white">Earn XP, badges, and rewards as you learn</span>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" className="btn-premium text-lg px-8 py-4 inline-flex items-center gap-2">
                  Start Learning
                  <ArrowRightIcon className="w-5 h-5" />
                </Link>
                <Link to="/how-it-works" className="btn-glass text-lg px-8 py-4">
                  Watch Gameplay
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="card-premium p-8">
                <div className="text-center mb-6">
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[var(--color-electric-blue)] to-[var(--color-violet)] p-6 mx-auto mb-4">
                    <AcademicCapIcon className="w-full h-full text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">Your Profile</h3>
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-[var(--color-amber)]">Level 12</span>
                    <span className="text-[var(--color-light-gray)]">•</span>
                    <span className="text-[var(--color-electric-blue)]">4,250 XP</span>
                  </div>
                </div>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center justify-between p-2 bg-white/5 rounded-lg">
                    <span className="text-white text-sm">Daily Streak</span>
                    <div className="flex items-center gap-1">
                      <FireIcon className="w-4 h-4 text-[var(--color-orange)]" />
                      <span className="text-[var(--color-orange)] font-bold">7 days</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-2 bg-white/5 rounded-lg">
                    <span className="text-white text-sm">Class Rank</span>
                    <span className="text-[var(--color-emerald)] font-bold">#3</span>
                  </div>
                  <div className="flex items-center justify-between p-2 bg-white/5 rounded-lg">
                    <span className="text-white text-sm">Achievements</span>
                    <span className="text-[var(--color-violet)] font-bold">15/50</span>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="text-center p-2 bg-gradient-to-br from-[var(--color-amber)]/20 to-[var(--color-amber)]/10 rounded-lg">
                    <div className="text-2xl mb-1">🏆</div>
                    <div className="text-xs text-[var(--color-amber)]">Champion</div>
                  </div>
                  <div className="text-center p-2 bg-gradient-to-br from-[var(--color-violet)]/20 to-[var(--color-violet)]/10 rounded-lg">
                    <div className="text-2xl mb-1">🧙</div>
                    <div className="text-xs text-[var(--color-violet)]">Wizard</div>
                  </div>
                  <div className="text-center p-2 bg-gradient-to-br from-[var(--color-emerald)]/20 to-[var(--color-emerald)]/10 rounded-lg">
                    <div className="text-2xl mb-1">⚡</div>
                    <div className="text-xs text-[var(--color-emerald)]">Speedster</div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </Container>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-[var(--color-dark-navy)]">
        <Container>
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-white mb-4">
              Why You'll Love <span className="text-gradient">Lumen8</span>
            </h2>
            <p className="text-xl text-[var(--color-light-gray)] max-w-3xl mx-auto">
              It's like your favorite game, but you're leveling up your brain!
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="card-premium p-8 h-full hover-glow">
                  <feature.icon className="w-12 h-12 text-[var(--color-electric-blue)] mb-4" />
                  <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                  <p className="text-[var(--color-light-gray)]">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* Mastery Section */}
      <section className="py-20 bg-gradient-to-b from-[var(--color-dark-navy)] to-[var(--color-midnight)]">
        <Container>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-black text-white mb-6">
                We're Here to Help You <span className="text-gradient">Truly Master</span> Every Topic
              </h2>
              <p className="text-xl text-[var(--color-light-gray)] mb-8">
                Not just scrape by. Not just memorise for a test. Actually <em>understand</em> it —
                so it sticks with you and you can build on it.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[var(--color-electric-blue)]/20 flex items-center justify-center">
                    <CheckCircleIcon className="w-5 h-5 text-[var(--color-electric-blue)]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white mb-1">Keep practising until you've got it</h3>
                    <p className="text-[var(--color-light-gray)]">No moving on until you're ready. Take as many tries as you need.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[var(--color-violet)]/20 flex items-center justify-center">
                    <CheckCircleIcon className="w-5 h-5 text-[var(--color-violet)]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white mb-1">We help you remember long-term</h3>
                    <p className="text-[var(--color-light-gray)]">Spaced repetition brings back topics before you forget them.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[var(--color-emerald)]/20 flex items-center justify-center">
                    <CheckCircleIcon className="w-5 h-5 text-[var(--color-emerald)]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white mb-1">See exactly where you stand</h3>
                    <p className="text-[var(--color-light-gray)]">Your mastery level for every topic — no surprises on test day.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="card-premium p-8 text-center">
                <div className="text-7xl font-black text-gradient mb-4">100%</div>
                <div className="text-2xl font-bold text-white mb-2">Mastery Goal</div>
                <p className="text-[var(--color-light-gray)] mb-6">
                  We're not satisfied until you truly understand each concept
                </p>
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-white">Fractions</span>
                      <span className="text-[var(--color-emerald)]">Mastered!</span>
                    </div>
                    <div className="w-full bg-white/10 rounded-full h-2">
                      <div className="bg-[var(--color-emerald)] h-2 rounded-full" style={{ width: '100%' }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-white">Algebra Basics</span>
                      <span className="text-[var(--color-electric-blue)]">85%</span>
                    </div>
                    <div className="w-full bg-white/10 rounded-full h-2">
                      <div className="bg-[var(--color-electric-blue)] h-2 rounded-full" style={{ width: '85%' }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-white">Quadratics</span>
                      <span className="text-[var(--color-amber)]">Learning...</span>
                    </div>
                    <div className="w-full bg-white/10 rounded-full h-2">
                      <div className="bg-[var(--color-amber)] h-2 rounded-full" style={{ width: '45%' }}></div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-[var(--color-emerald)] rounded-full filter blur-3xl opacity-30"></div>
            </div>
          </div>
        </Container>
      </section>

      {/* Learning Modes */}
      <section className="py-20 bg-[var(--color-midnight)]">
        <Container>
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-white mb-4">
              Choose Your <span className="text-gradient">Style</span>
            </h2>
            <p className="text-xl text-[var(--color-light-gray)] max-w-3xl mx-auto">
              Different ways to learn, depending on how you're feeling
            </p>
          </div>
          <div className="grid lg:grid-cols-3 gap-8">
            {learningModes.map((mode, index) => (
              <motion.div
                key={mode.title}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="card-premium p-8 h-full text-center hover-glow">
                  <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${mode.gradient} p-5 mx-auto mb-6`}>
                    <SparklesIcon className="w-full h-full text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">{mode.title}</h3>
                  <p className="text-[var(--color-light-gray)] mb-6">{mode.description}</p>
                  <div className="space-y-2">
                    {mode.features.map((feature) => (
                      <div key={feature} className="flex items-center justify-center gap-2">
                        <CheckCircleIcon className="w-4 h-4 text-[var(--color-emerald)]" />
                        <span className="text-sm text-[var(--color-light-gray)]">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* Try Our Lessons - Subject Tabs */}
      <section className="py-20 bg-gradient-to-b from-[var(--color-midnight)] to-[var(--color-dark-navy)]">
        <Container>
          <div className="text-center mb-12">
            <h2 className="text-4xl font-black text-white mb-4">
              Try Our <span className="text-gradient">Interactive Lessons</span>
            </h2>
            <p className="text-xl text-[var(--color-light-gray)] max-w-3xl mx-auto">
              Experience Year 8 curriculum brought to life with characters, stories, and interactive challenges
            </p>
          </div>

          {/* Subject Tabs */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex p-1.5 rounded-2xl" style={{ background: 'rgba(30, 41, 59, 0.8)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <button
                onClick={() => setActiveSubjectTab('english')}
                className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all ${
                  activeSubjectTab === 'english'
                    ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                <BookOpenIcon className="w-5 h-5" />
                English
              </button>
              <button
                onClick={() => setActiveSubjectTab('maths')}
                className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all ${
                  activeSubjectTab === 'maths'
                    ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-lg'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                <CalculatorIcon className="w-5 h-5" />
                Maths
              </button>
              <button
                onClick={() => setActiveSubjectTab('science')}
                className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all ${
                  activeSubjectTab === 'science'
                    ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-lg'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                <BeakerIcon className="w-5 h-5" />
                Science
              </button>
            </div>
          </div>

          {/* Lesson Cards */}
          <div className="max-w-4xl mx-auto">
            {activeSubjectTab === 'english' && (
              <motion.div
                key="english"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="card-premium p-8 hover-glow">
                  <div className="flex flex-col lg:flex-row gap-8 items-center">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-4">
                        <span className="px-3 py-1 rounded-full text-xs font-bold bg-blue-500/20 text-blue-400">YEAR 8</span>
                        <span className="px-3 py-1 rounded-full text-xs font-bold bg-purple-500/20 text-purple-400">STORY MODE</span>
                      </div>
                      <h3 className="text-2xl font-bold text-white mb-3">Persuasive Techniques in Advertising</h3>
                      <p className="text-[var(--color-light-gray)] mb-4">
                        Join Maya and Professor Chen as they decode the hidden persuasion techniques in a mysterious advertisement.
                        Learn to identify bandwagon appeal, emotive language, and more!
                      </p>
                      <div className="flex flex-wrap gap-2 mb-6">
                        <span className="px-2 py-1 rounded text-xs bg-white/5 text-gray-300">Bandwagon Appeal</span>
                        <span className="px-2 py-1 rounded text-xs bg-white/5 text-gray-300">Statistics Analysis</span>
                        <span className="px-2 py-1 rounded text-xs bg-white/5 text-gray-300">Emotive Language</span>
                        <span className="px-2 py-1 rounded text-xs bg-white/5 text-gray-300">Creating Urgency</span>
                      </div>
                      <div className="flex items-center gap-6 text-sm text-gray-400 mb-6">
                        <div className="flex items-center gap-2">
                          <ClockIcon className="w-4 h-4" />
                          <span>~10 mins</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <StarIcon className="w-4 h-4 text-amber-400" />
                          <span>+120 XP</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <SparklesIcon className="w-4 h-4 text-purple-400" />
                          <span>5 Questions</span>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setActiveLessonType('english');
                          setIsLessonModalOpen(true);
                        }}
                        className="btn-premium px-6 py-3 inline-flex items-center gap-2"
                      >
                        <PlayIcon className="w-5 h-5" />
                        Start Lesson
                      </button>
                    </div>
                    <div className="w-48 h-48 rounded-2xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 flex items-center justify-center">
                      <div className="text-center">
                        <div className="text-6xl mb-2">👩‍🎓</div>
                        <div className="text-3xl">👨‍🏫</div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {activeSubjectTab === 'maths' && (
              <motion.div
                key="maths"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="card-premium p-8 hover-glow">
                  <div className="flex flex-col lg:flex-row gap-8 items-center">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-4">
                        <span className="px-3 py-1 rounded-full text-xs font-bold bg-purple-500/20 text-purple-400">YEAR 8</span>
                        <span className="px-3 py-1 rounded-full text-xs font-bold bg-pink-500/20 text-pink-400">INTERACTIVE</span>
                      </div>
                      <h3 className="text-2xl font-bold text-white mb-3">Algebraic Proofs & Exponential Functions</h3>
                      <p className="text-[var(--color-light-gray)] mb-4">
                        Discover the beauty of mathematical proofs with Alex and Dr. Euler! Learn why (a+b)² = a² + 2ab + b²
                        through visual geometry, then explore exponential decay with interactive graph builders.
                      </p>
                      <div className="flex flex-wrap gap-2 mb-6">
                        <span className="px-2 py-1 rounded text-xs bg-white/5 text-gray-300">Visual Proofs</span>
                        <span className="px-2 py-1 rounded text-xs bg-white/5 text-gray-300">Algebraic Identities</span>
                        <span className="px-2 py-1 rounded text-xs bg-white/5 text-gray-300">Exponential Functions</span>
                        <span className="px-2 py-1 rounded text-xs bg-white/5 text-gray-300">Interactive Graphs</span>
                      </div>
                      <div className="flex items-center gap-6 text-sm text-gray-400 mb-6">
                        <div className="flex items-center gap-2">
                          <ClockIcon className="w-4 h-4" />
                          <span>~15 mins</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <StarIcon className="w-4 h-4 text-amber-400" />
                          <span>+160 XP</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <SparklesIcon className="w-4 h-4 text-purple-400" />
                          <span>6 Questions + 2 Challenges</span>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setActiveLessonType('maths');
                          setIsLessonModalOpen(true);
                        }}
                        className="btn-premium px-6 py-3 inline-flex items-center gap-2"
                      >
                        <PlayIcon className="w-5 h-5" />
                        Start Lesson
                      </button>
                    </div>
                    <div className="w-48 h-48 rounded-2xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center">
                      <div className="text-center">
                        <div className="text-6xl mb-2">🧑‍💻</div>
                        <div className="text-3xl">👩‍🔬</div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {activeSubjectTab === 'science' && (
              <motion.div
                key="science"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="card-premium p-8 text-center">
                  <div className="w-24 h-24 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 flex items-center justify-center">
                    <BeakerIcon className="w-12 h-12 text-emerald-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">Science Lessons Coming Soon!</h3>
                  <p className="text-[var(--color-light-gray)] max-w-md mx-auto">
                    We're working on amazing interactive science lessons covering physics, chemistry, and biology.
                    Stay tuned for experiments, simulations, and discovery-based learning!
                  </p>
                </div>
              </motion.div>
            )}
          </div>
        </Container>
      </section>

      {/* Sample Question Preview */}
      <section className="py-20 bg-[var(--color-dark-navy)]">
        <Container>
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-white mb-4">
              Learn Through <span className="text-gradient">Doing</span>
            </h2>
            <p className="text-xl text-[var(--color-light-gray)] max-w-3xl mx-auto">
              Interactive questions that adapt to your level
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Interactive Question - Lesson Preview */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <div className="flex flex-col items-center">
                <LessonPreview onClick={() => {
                  setActiveLessonType('story');
                  setIsLessonModalOpen(true);
                }} />
                <div className="mt-4 text-center">
                  <h3 className="text-lg font-semibold text-white">Interactive Question</h3>
                  <p className="text-sm text-[var(--color-light-gray)]">Multiple choice, drag-and-drop, and more</p>
                </div>
              </div>
            </motion.div>

            {/* Instant Feedback Preview */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="flex flex-col items-center">
                <InstantFeedbackPreview />
                <div className="mt-4 text-center">
                  <h3 className="text-lg font-semibold text-white">Instant Feedback</h3>
                  <p className="text-sm text-[var(--color-light-gray)]">Know right away with helpful explanations</p>
                </div>
              </div>
            </motion.div>
          </div>
        </Container>
      </section>

      {/* Progress Visualization */}
      <section className="py-20 bg-[var(--color-dark-navy)]">
        <Container>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-black text-white mb-8">
                Watch Your <span className="text-gradient">Progress Grow</span>
              </h2>
              <p className="text-xl text-[var(--color-light-gray)] mb-8">
                See exactly how much you've learned with cool charts and progress bars.
                Every question you answer makes you stronger!
              </p>
              <div className="space-y-4">
                {subjects.map((subject) => (
                  <div key={subject.name}>
                    <div className="flex justify-between mb-2">
                      <span className="text-white font-semibold">{subject.name}</span>
                      <span className="text-[var(--color-electric-blue)]">{subject.mastery}%</span>
                    </div>
                    <div className="w-full bg-white/10 rounded-full h-3">
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: `${subject.mastery}%` }}
                        transition={{ duration: 1, delay: 0.2 }}
                        viewport={{ once: true }}
                        className="h-3 rounded-full"
                        style={{
                          background: `linear-gradient(to right, ${subject.color}, ${subject.color}dd)`,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="glass-card p-8 rounded-2xl text-center">
                <GlobeAltIcon className="w-16 h-16 text-[var(--color-electric-blue)] mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-white mb-4">Global Leaderboard</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-gradient-to-r from-[var(--color-amber)]/20 to-[var(--color-amber)]/10 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">🥇</span>
                      <span className="text-white font-semibold">Alex</span>
                    </div>
                    <span className="text-[var(--color-amber)] font-bold">12,450 XP</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">🥈</span>
                      <span className="text-white font-semibold">Sarah</span>
                    </div>
                    <span className="text-[var(--color-light-gray)] font-bold">11,200 XP</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gradient-to-r from-[var(--color-electric-blue)]/20 to-[var(--color-electric-blue)]/10 rounded-lg border-2 border-[var(--color-electric-blue)]">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">🥉</span>
                      <span className="text-white font-semibold">You</span>
                    </div>
                    <span className="text-[var(--color-electric-blue)] font-bold">10,850 XP</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-[var(--color-dark-navy)] to-[var(--color-midnight)]">
        <Container>
          <div className="text-center max-w-3xl mx-auto">
            <AcademicCapIcon className="w-16 h-16 text-[var(--color-electric-blue)] mx-auto mb-6" />
            <h2 className="text-4xl font-black text-white mb-6">
              Ready to <span className="text-gradient">Master</span> Your Subjects?
            </h2>
            <p className="text-xl text-[var(--color-light-gray)] mb-8">
              Learn at your own pace, get help whenever you need it, and actually understand what you're learning.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-premium text-lg px-8 py-4 inline-flex items-center gap-2">
                Start Learning
                <ArrowRightIcon className="w-5 h-5" />
              </Link>
              <Link to="/how-it-works" className="btn-glass text-lg px-8 py-4">
                See How It Works
              </Link>
            </div>
            <p className="mt-8 text-sm text-[var(--color-medium-gray)]">
              Ask your teacher or parents about getting Lumen8!
            </p>
          </div>
        </Container>
      </section>

      {/* Interactive Lesson Modal */}
      <InteractiveModal
        isOpen={isLessonModalOpen}
        onClose={() => setIsLessonModalOpen(false)}
      >
        {activeLessonType === 'english' && (
          <Year8EnglishLesson onClose={() => setIsLessonModalOpen(false)} />
        )}
        {activeLessonType === 'maths' && (
          <Year8MathsLesson onClose={() => setIsLessonModalOpen(false)} />
        )}
        {activeLessonType === 'story' && (
          <StoryLesson onClose={() => setIsLessonModalOpen(false)} />
        )}
      </InteractiveModal>
    </>
  );
};

export default ForStudents;